import { LocaleCode, formatNumberColumnValue, safeParseFormatOptions } from '@doss/common-all'
import { FormatPreset } from '@doss/prisma/types'
import { IconButton } from '@mui/material'
import { ComponentType, FocusEvent, useState } from 'react'
import { Tb123, TbKeyboard } from 'react-icons/tb'
import { NumericFormat, type NumberFormatValues } from 'react-number-format'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { BaseInputProps } from '../base/types'
import { ShortTextInput } from '../text/short-text-input'

export interface NumberFormatInputProps extends BaseInputProps {
  /** Raw numeric value held by the host; the preset formatting is display-only. */
  value: number | null
  onChange: (value: number | null) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  /** Display formatting; when absent the field behaves as a plain number field. */
  formatPreset: FormatPreset | null | undefined
  /** Preset-specific options (currency, unit, decimal places, …); parsed per `formatPreset`. */
  formatOptions: unknown
  locale: LocaleCode
  /** Currency used when a NUMBER_CURRENCY preset doesn't carry its own. */
  defaultCurrency?: string
  /** Rendered as the input's `name` attribute (browser autofill / form association). */
  name?: string
  /** When true, mount directly in the raw editing state instead of the formatted display. */
  autoFocus?: boolean
}

/**
 * Shared number field for Tables and Formv2 forms. While focused it edits the raw number in a
 * `NumericFormat` input that pins the currency symbol / unit as an adornment and applies the preset's
 * grouping and decimal separators as you type; when blurred (or read-only) it renders the fully
 * formatted value. The value handed to `onChange` is always the raw number — the formatting is purely
 * visual.
 *
 * Uses a text input (not `type="number"`) so the formatted string — which may include grouping
 * separators, a currency symbol, or a unit suffix — can be displayed.
 */
export const NumberFormatInput = ({
  value,
  onChange,
  onBlur,
  formatPreset,
  formatOptions,
  locale,
  defaultCurrency,
  name,
  autoFocus = false,
  disabled,
  readOnly,
  placeholder,
  ...wrapperProps
}: NumberFormatInputProps) => {
  const { isMobile } = useDeviceType()
  // Alphanumeric keyboard vs. numeric keypad toggle on mobile.
  const [useNumericKeypad, setUseNumericKeypad] = useState(true)
  // Show the formatted value until the field is focused, then swap to the raw editable input.
  const [isEditing, setIsEditing] = useState(autoFocus)

  const nonEditable = Boolean(disabled) || Boolean(readOnly)
  // Guard NaN (not just null) so a non-numeric stored value falls through to the editable input
  // rather than rendering a blank, non-editable formatted display.
  const hasValue = value !== null && !Number.isNaN(value)

  const currencyResult =
    formatPreset === FormatPreset.NUMBER_CURRENCY
      ? safeParseFormatOptions(FormatPreset.NUMBER_CURRENCY, formatOptions)
      : null
  const currencyFormat = currencyResult?.success ? currencyResult.data : null
  const inputCurrency = currencyFormat?.currency ?? defaultCurrency
  const showCurrency = currencyFormat !== null && inputCurrency !== undefined

  const unitResult =
    formatPreset === FormatPreset.NUMBER_UNIT
      ? safeParseFormatOptions(FormatPreset.NUMBER_UNIT, formatOptions)
      : null
  const unitFormat = unitResult?.success ? unitResult.data : null

  // Show the formatted (non-interactive) value when the field has a value and isn't being edited;
  // focusing an editable field swaps to the raw input below. `readOnly={!nonEditable}` keeps an
  // *editable* field's formatted display non-typable so onFocus can swap it to the real input,
  // while a nonEditable field is `disabled` outright.
  if (hasValue && (nonEditable || !isEditing)) {
    return (
      <ShortTextInput
        {...wrapperProps}
        disabled={nonEditable}
        name={name}
        onFocus={nonEditable ? undefined : () => setIsEditing(true)}
        placeholder={placeholder}
        readOnly={!nonEditable}
        value={formatNumberColumnValue(value, { formatPreset, formatOptions }, locale)}
      />
    )
  }

  const inputLocale = currencyFormat?.locale ?? locale
  const currencyDecimalScale =
    currencyFormat?.decimalPlaces ??
    (showCurrency && inputCurrency
      ? new Intl.NumberFormat(inputLocale, {
          style: 'currency',
          currency: inputCurrency,
        }).resolvedOptions().maximumFractionDigits
      : undefined)

  // Derive the locale's currency symbol (and its position) plus the grouping/decimal separators from
  // Intl so the editable input matches the formatted display and the host locale.
  const numberParts =
    showCurrency && inputCurrency
      ? new Intl.NumberFormat(inputLocale, {
          style: 'currency',
          currency: inputCurrency,
        }).formatToParts(1234.56)
      : new Intl.NumberFormat(locale).formatToParts(1234.56)

  const currencySymbol = numberParts.find((part) => part.type === 'currency')?.value
  const currencyPosition = numberParts.findIndex((part) => part.type === 'currency')
  const integerPosition = numberParts.findIndex((part) => part.type === 'integer')
  const isCurrencySuffix = currencyPosition > integerPosition
  const thousandSeparator = numberParts.find((part) => part.type === 'group')?.value || ','
  const decimalSeparator = numberParts.find((part) => part.type === 'decimal')?.value || '.'

  const currencyAdornment = currencySymbol ? (
    <div className={isCurrencySuffix ? '-ms-2' : '-me-2'}>{currencySymbol}</div>
  ) : null
  const startAdornment = isCurrencySuffix ? null : currencyAdornment

  const unitAdornment = unitFormat ? <div className="-ms-2">{unitFormat.unit}</div> : null
  const trailingAdornment = isCurrencySuffix ? currencyAdornment : unitAdornment

  // On mobile, offer a toggle between the numeric keypad and the full keyboard.
  const keyboardToggle = isMobile ? (
    <IconButton
      onClick={() => setUseNumericKeypad(!useNumericKeypad)}
      size="small"
      title={useNumericKeypad ? 'Switch to full keyboard' : 'Switch to numeric keypad'}
    >
      {useNumericKeypad ? <TbKeyboard size={16} /> : <Tb123 size={16} />}
    </IconButton>
  ) : null
  const endAdornment =
    trailingAdornment || keyboardToggle ? (
      <div className="flex items-center gap-2">
        {trailingAdornment}
        {keyboardToggle}
      </div>
    ) : null

  // Apply the currency/unit preset's decimal and grouping options to the editable input so the typed
  // value lines up with the formatted display shown when not editing.
  const editingDecimalScale = showCurrency ? currencyDecimalScale : unitFormat?.decimalPlaces
  const editingDecimalSeparator = showCurrency || unitFormat ? decimalSeparator : undefined
  const editingThousandSeparator =
    showCurrency || unitFormat?.thousandsSeparator ? thousandSeparator : undefined

  // NumericFormat<customInput> produces an overly complex union type in React 19 due to generic
  // inference limitations (TS2590). Cast to a simpler type to avoid the inference explosion.
  const NumericFormatComponent = NumericFormat as ComponentType<Record<string, unknown>>

  return (
    <NumericFormatComponent
      {...wrapperProps}
      autoFocus={autoFocus || isEditing}
      customInput={ShortTextInput}
      decimalScale={editingDecimalScale}
      decimalSeparator={editingDecimalSeparator}
      disabled={nonEditable}
      endAdornment={endAdornment}
      fixedDecimalScale={showCurrency}
      inputMode={isMobile && useNumericKeypad ? 'decimal' : 'text'}
      name={name}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        onBlur?.(event)
        setIsEditing(false)
      }}
      onFocus={() => setIsEditing(true)}
      onValueChange={(values: NumberFormatValues) => {
        onChange(values.floatValue ?? null)
      }}
      pattern={isMobile && useNumericKeypad ? '[0-9]*' : undefined}
      placeholder={placeholder}
      startAdornment={startAdornment}
      thousandSeparator={editingThousandSeparator}
      value={value ?? ''}
    />
  )
}
